import { lazy, Suspense, useState, type CSSProperties } from 'react'
import BottomNav from '../components/BottomNav'
import type { Tab } from '../components/BottomNav'
import { RISE_SPACE, TOTAL_HEIGHT } from '../components/bottomNavNotch'
import { tabOrder } from '../components/navTabs'
import TabPlaceholder from '../components/TabPlaceholder'
import LeagueDetail from '../components/LeagueDetail'
import ExploreLeaguesScreen from '../components/exploreLeagues/ExploreLeaguesScreen'
import Settings from '../components/Settings'
import SharedElementOverlay from '../components/SharedElementOverlay'
import SwipeBackPage from '../components/SwipeBackPage'
import PullToRefresh from '../components/PullToRefresh'
import Toast from '../components/Toast'
import RivalChallengeBanner from '../components/RivalChallengeBanner'
import InstallPromptSheet from '../components/InstallPromptSheet'
import { useDragNav } from '../useDragNav'
import { logout } from '../api/auth'
import { markAccountDeleted } from '../storage'
import HowToPlayScreen from '../features/rules/HowToPlayScreen'
import { useRules } from '../features/rules/rulesContext'
import TabPage from './TabPages'
import AppModals from './AppModals'
import OtherPlayerScreen from './OtherPlayerScreen'
import type { AppView } from './AppView'

/**
 * The backoffice.
 *
 * **A dynamic `import()`, so the bundle is not in a non-admin's app at all** — not a `hidden`
 * class, not a `return null`. It is the only `React.lazy` in the repo, and the pattern is here
 * rather than anywhere else because this is the one screen most users must never receive. It is
 * still defence in depth: the control is the server's 403 on every `/api/admin` route.
 */
const BackofficeScreen = lazy(() => import('../components/backoffice/BackofficeScreen'))

/**
 * The screen switch: a hand-rolled router whose "route" is whichever piece
 * of state is set. The order of the early returns below is the precedence — another player's
 * profile beats an open league, which beats settings, which beats the tab shell — and it is load
 * bearing, since more than one can be set at a time (opening a league member's profile leaves
 * `selectedLeagueId` set so backing out returns to the league).
 *
 * Tab state lives here rather than in App: nothing above the render reads it.
 */
export default function AppRoutes({ view }: { view: AppView }) {
  const [activeTab, setActiveTab] = useState<Tab>('predict')
  // Which physical page-slot div below currently plays the "primary" role. A landed swipe flips
  // this instead of always rendering activeTab into the same div: rendering activeTab
  // into a fixed div forced the just-slid-in, already-mounted neighbour page to unmount and
  // remount fresh into the primary div at the exact moment the swipe landed, producing one
  // visible frame before its layout (e.g. the predict tab's centered empty-state CTA) resolved.
  // Flipping which div is primary lets that div's existing content and layout carry over.
  const [primarySlot, setPrimarySlot] = useState<'A' | 'B'>('A')
  const [isViewingSettings, setIsViewingSettings] = useState(false)
  const [isViewingBackoffice, setIsViewingBackoffice] = useState(false)
  /**
   * The fixture a pressed game card asked for. Held here rather than in `TabPage`,
   * which the drag-nav shell renders twice at once (primary slot plus mid-swipe neighbour) — state
   * held there exists in two copies and the wrong one can win.
   */
  const [focusedFixtureId, setFocusedFixtureId] = useState<string | null>(null)
  const rules = useRules()

  const handleTabChange = (tab: Tab) => {
    // The refresh hangs on the arrival, not on a mount. Both entry points funnel
    // here — the nav tap and the drag-nav's `onCommit` — and an abandoned swipe never does, which
    // is what a `useEffect` in the page itself can't tell apart: the neighbour tab is already
    // mounted while the finger is still dragging (the slot flip).
    if (tab === 'leagues' && tab !== activeTab) {
      void view.leaguesRefresh.refresh('navigation')
    }
    setActiveTab(tab)
    view.predictions.closeEdit()
    view.leagueNav.close()
    view.exploreLeagues.close()
    view.history.close()
    view.viewedPlayer.clearViewedPlayer()
  }

  /** Press a live or settled card on the predict grid → the Fixtures tab, on that fixture. The tab
   *  change goes through `handleTabChange` rather than `setActiveTab` so the predict tab's open
   *  edit sheet is closed on the way out, instead of being left under the fixtures page. */
  const openFixture = (gameId: string) => {
    setFocusedFixtureId(gameId)
    handleTabChange('fixtures')
  }

  const {
    containerRef: dragNavRef,
    drag,
    onSettled,
  } = useDragNav({
    activeTab,
    tabOrder,
    // A swipe landing flips which slot is primary in the same commit as the tab change, so the
    // slot that already has the destination page mounted just keeps it.
    onCommit: (tab) => {
      setPrimarySlot((prev) => (prev === 'A' ? 'B' : 'A'))
      handleTabChange(tab)
    },
    excludeSelector: '.chip-row',
  })

  const toast = view.toastMessage && (
    <Toast
      message={view.toastMessage}
      onDismiss={() => view.setToastMessage(null)}
    />
  )

  const challengeBanner = view.rivals.bannerRequest && (
    <RivalChallengeBanner
      request={view.rivals.bannerRequest}
      onAccept={view.rivals.acceptRequest}
      onDecline={view.rivals.declineRequest}
      onDismiss={view.rivals.dismissRequest}
    />
  )

  // Highest precedence, ahead of `viewedPlayer`: an operator who opens this
  // mid-matchday must not have a league detail underneath deciding what they see.
  if (isViewingBackoffice && view.identity.isAdmin) {
    return (
      <div className="app app--pushed">
        <Suspense fallback={<p className="app__status-message">Loading backoffice…</p>}>
          <BackofficeScreen onBack={() => setIsViewingBackoffice(false)} />
        </Suspense>
      </div>
    )
  }

  const player = view.viewedPlayer.viewedPlayer
  if (player) {
    return (
      <div className="app app--pushed">
        <OtherPlayerScreen view={view} player={player} />
        {challengeBanner}
        {toast}
      </div>
    )
  }

  if (view.leagueNav.selectedLeagueId) {
    const league = view.leagues.leagues.find(
      (l) => l.id === view.leagueNav.selectedLeagueId
    )!

    return (
      <div className="app app--pushed">
        <SwipeBackPage onBack={view.leagueNav.transition.beginBack}>
          {/* The detail reads its league out of the same array, so its pull runs the
              same refresh the list's does and the prop updates underneath it. Off while the
              shared-element flight is running — that gesture owns the screen. */}
          <PullToRefresh
            onRefresh={() => view.leaguesRefresh.refresh('pull')}
            enabled={!view.leagueNav.transition.isTransitioning}
          >
            <LeagueDetail
              league={league}
              rivals={view.rivals.rivals}
              seasonStartLabel={view.scores.seasonStartLabel}
              entryTransition={{
                state: view.leagueNav.transition.detailEntry,
                nameRef: view.leagueNav.transition.registerDestAnchor,
              }}
              onBack={view.leagueNav.transition.beginBack}
              onLeaveLeague={view.leagues.leaveLeague}
              onInviteToLeague={view.leagues.inviteToLeague}
              onSaveMotto={view.leagues.saveMotto}
              joinRequests={view.joinRequests.requests}
              onAcceptJoinRequest={view.joinRequests.accept}
              onDeclineJoinRequest={view.joinRequests.decline}
              onChangeJoinPolicy={view.joinRequests.setPolicy}
              onViewProfile={view.viewedPlayer.viewPlayer}
              onToast={view.setToastMessage}
            />
          </PullToRefresh>
        </SwipeBackPage>
        <SharedElementOverlay
          data={view.leagueNav.transition.overlay}
          cloneRef={view.leagueNav.transition.cloneRef}
        />
        {challengeBanner}
        {toast}
      </div>
    )
  }

  // Below the league detail on purpose: opening a league from Explore stacks the
  // detail on top, and backing out of it lands on the directory the user was browsing.
  if (view.exploreLeagues.isOpen) {
    const explore = view.exploreLeagues
    return (
      <div className="app app--pushed">
        <SwipeBackPage onBack={explore.close}>
          <ExploreLeaguesScreen
            query={explore.query}
            onQueryChange={explore.setQuery}
            results={explore.results}
            isLoading={explore.isLoading}
            hasMore={explore.hasMore}
            onLoadMore={explore.loadMore}
            onJoin={explore.join}
            onSelectLeague={view.leagueNav.open}
            onBack={explore.close}
          />
        </SwipeBackPage>
        {challengeBanner}
        {toast}
      </div>
    )
  }

  // Same tier as settings in the early-return order: both are pushed screens
  // reached from the profile's utility list, and neither can be open under the other.
  if (rules.isScreenOpen) {
    return (
      <div className="app app--pushed">
        <SwipeBackPage onBack={rules.closeScreen}>
          <HowToPlayScreen
            initialSection={rules.screenSection}
            onBack={rules.closeScreen}
          />
        </SwipeBackPage>
      </div>
    )
  }

  if (isViewingSettings) {
    return (
      <div className="app app--pushed">
        <SwipeBackPage onBack={() => setIsViewingSettings(false)}>
          <Settings
            displayName={view.identity.displayName}
            onSaveDisplayName={view.identity.saveDisplayName}
            themePreference={view.theme.themePreference}
            onChangeTheme={view.theme.changeTheme}
            email={view.identity.accountEmail}
            isAdmin={view.identity.isAdmin}
            onOpenBackoffice={() => {
              setIsViewingSettings(false)
              setIsViewingBackoffice(true)
            }}
            onLogout={async () => {
              await logout()
              window.location.reload()
            }}
            /* The cascade already deleted the session rows, so the cookie is dead
               server-side — `logout()` clears the browser's stale copy rather than leaving it to
               401 on the next request. The flag is what makes the reload say something. */
            onAccountDeleted={async () => {
              markAccountDeleted()
              await logout()
              window.location.reload()
            }}
            onBack={() => setIsViewingSettings(false)}
          />
        </SwipeBackPage>
      </div>
    )
  }

  const renderTab = (tab: Tab) => (
    <TabPage
      view={view}
      tab={tab}
      onOpenSettings={() => setIsViewingSettings(true)}
      onOpenFixture={openFixture}
      focusedFixtureId={focusedFixtureId}
      onFixtureFocusConsumed={() => setFocusedFixtureId(null)}
    />
  )

  return (
    <div
      className={`app app--with-nav ${drag.phase === 'settling' ? 'app--settling' : ''}`}
      // The bar's height, from the same constant that builds its notch path — App.css adds the
      // safe-area inset on top (only CSS can read it) to size the bottom reserve..
      // --nav-rise-space is the slice of that height sitting *above* the bar's flat top edge (the
      // room the notch rises into), exported so the desktop rails in App.css can stop on the edge
      // itself rather than --nav-dock's top. BottomNav sets the same constant as --rise-space, but
      // only on itself, so it isn't readable from here..
      style={
        {
          '--nav-dock': `${TOTAL_HEIGHT}px`,
          '--nav-rise-space': `${RISE_SPACE}px`,
        } as CSSProperties
      }
    >
      <div
        className={`app__pages ${drag.phase !== 'idle' ? 'app__pages--dragging' : ''}`}
        ref={dragNavRef}
      >
        {(['A', 'B'] as const).map((slot) =>
          slot === primarySlot ? (
            <div
              key={slot}
              className="app__page-slot"
              style={{
                transform: drag.currentTransform,
                transition: drag.animate
                  ? 'transform var(--motion-base) var(--ease-out)'
                  : 'none',
              }}
              onTransitionEnd={
                drag.phase === 'settling' ? onSettled : undefined
              }
            >
              {renderTab(activeTab)}
            </div>
          ) : (
            drag.neighbourTab && (
              <div
                key={slot}
                className="app__page-slot app__page-slot--neighbour"
                style={{
                  transform: drag.neighbourTransform,
                  transition: drag.animate
                    ? 'transform var(--motion-base) var(--ease-out)'
                    : 'none',
                }}
              >
                {drag.committing ? (
                  renderTab(drag.neighbourTab)
                ) : (
                  <TabPlaceholder tab={drag.neighbourTab} />
                )}
              </div>
            )
          )
        )}
      </div>

      <BottomNav
        // Once a swipe has committed, let the nav (and its notch) jump to the destination tab
        // immediately, in step with the page content's settle transition starting — waiting for
        // `activeTab` itself left the notch visibly trailing by a full animation window. A direct
        // tap is unaffected, since drag.phase stays 'idle' there.
        activeTab={
          drag.phase === 'settling' && drag.committing && drag.neighbourTab
            ? drag.neighbourTab
            : activeTab
        }
        onTabChange={handleTabChange}
        pendingRivalRequestsCount={view.rivals.incomingRequests.length}
      />

      <AppModals view={view} />

      {/* Only in the tab shell: the sheet docks onto the bottom nav, and every branch above this
          one is a pushed screen that doesn't render it. */}
      <InstallPromptSheet controller={view.installPrompt} />

      {challengeBanner}
      {toast}
    </div>
  )
}
